/**
 * Signing policy — bundle-integrity scheme per tenant.
 *
 * Per ADR-0014 CRIT-4: HSM + FIPS dual-sign is NOT a v1 baseline. The
 * commercial default is software-only Ed25519. Tenants that activate ITAR
 * or FedRAMP frameworks auto-upgrade to dual-sign with FIPS-validated
 * software modules; admins can manually opt into HSM-required for the
 * highest assurance posture.
 *
 * Important: this policy governs **Viaanix→customer artifact integrity**
 * (bundle import/export). It is unrelated to user authentication — SSO,
 * OIDC, SAML, RSA-token, Entra etc. continue to work without HSM, per
 * PLATFORM-SPEC §6.
 */

import { z } from 'zod';
import { type ComplianceFramework } from './identity-field-policy.js';

export const SigningPolicySchema = z.enum([
  /**
   * Software-only Ed25519. Tenant key lives in `tenant_signing_keys` row,
   * KMS-encrypted at rest. No HSM. Suitable for commercial customers.
   */
  'ed25519_only',
  /**
   * Dual signature: Ed25519 + RSA-4096 produced under a FIPS 140-3
   * software cryptographic module. Required for ITAR + FedRAMP.
   */
  'ed25519_plus_rsa4096_fips',
  /**
   * Keys must live in a customer-controlled HSM (PKCS#11). Highest
   * assurance — opt-in via admin.
   */
  'hsm_required',
]);
export type SigningPolicy = z.infer<typeof SigningPolicySchema>;

/**
 * Frameworks that **mandate** at least the FIPS dual-signature posture.
 * Used by the auto-upgrade rule below.
 */
const FIPS_REQUIRED_FRAMEWORKS = new Set<ComplianceFramework>([
  'itar',
  // 'fedramp' — would belong here when FedRAMP joins ComplianceFrameworkSchema
]);

/**
 * Decide the minimum signing policy a tenant MUST be on, given the
 * frameworks it has activated. The actual stored policy may be stricter
 * (an admin can manually escalate to `hsm_required`) but never weaker.
 *
 * Pure function — no I/O. The auto-upgrade caller fetches frameworks +
 * current policy from `governance_configs`, runs this resolver, and
 * UPDATEs only if `current` < `minimum` (in the rank order below).
 */
export function minimumSigningPolicy(
  activeFrameworks: readonly ComplianceFramework[]
): SigningPolicy {
  for (const fw of activeFrameworks) {
    if (FIPS_REQUIRED_FRAMEWORKS.has(fw)) return 'ed25519_plus_rsa4096_fips';
  }
  return 'ed25519_only';
}

/**
 * Policy ranking — higher number = stricter. Used to compare two policies.
 */
const POLICY_RANK: Record<SigningPolicy, number> = {
  ed25519_only: 0,
  ed25519_plus_rsa4096_fips: 1,
  hsm_required: 2,
};

/**
 * Returns the stricter of two policies. Use this when reconciling the
 * framework-mandated minimum against the admin-chosen value — never let
 * activation lower the posture.
 */
export function maxSigningPolicy(a: SigningPolicy, b: SigningPolicy): SigningPolicy {
  return POLICY_RANK[a] >= POLICY_RANK[b] ? a : b;
}

/** Defensive parse used by the auto-upgrade caller. */
export const SigningPolicyValueSchema = SigningPolicySchema;
